import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CircleCheck, Info, Lock, Play, X } from 'lucide-react';
import { getLevelRows, gcomprisIconUrl } from '../../../utils/gcompris';
import GComprisCoinBadge from './GComprisCoinBadge';

function LevelRow({ level, status, coins, onPlay, rowRef }) {
  return (
    <li ref={rowRef} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2 shadow-sm">
      <span
        className={`w-[76px] shrink-0 rounded-lg px-2 py-1.5 text-center text-[13px] font-extrabold ${
          status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'
        }`}
      >
        Level {level}
      </span>

      <div className="flex-1 min-w-0 flex justify-end">
        {status === 'completed' && (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-100 px-3 py-1.5 text-[13px] font-bold text-emerald-700">
            <CircleCheck size={16} /> Completed
          </span>
        )}
        {status === 'play' && (
          <button
            type="button"
            onClick={onPlay}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-5 py-1.5 text-[13px] font-bold text-white shadow-sm hover:bg-indigo-700 transition-colors"
          >
            <Play size={15} fill="currentColor" /> Play
          </button>
        )}
        {status === 'locked' && (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-[13px] font-bold text-slate-500">
            <Lock size={14} /> Locked
          </span>
        )}
      </div>

      {coins > 0 && <GComprisCoinBadge amount={coins} />}
    </li>
  );
}

/**
 * The levels of one GCompris activity: which are completed, which one to play
 * next and (when GCompris fixes the count) which are still locked. `docked`
 * renders it as a plain card for the page layout; otherwise it is a centered
 * modal, portalled to <body> so the Learning Zone's scroll container can't clip it.
 */
// `coinsForLevel(level)` / `sameEveryLevel` come from gcomprisLevelRewards (the admin's coin settings).
export default function GComprisLevelsPanel({ activity, progress, coinsForLevel = () => 0, sameEveryLevel = 0, docked, onPlay, onClose }) {
  const playRowRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Activities with many levels (Maze has 25) scroll; start at the one to play.
  useEffect(() => {
    playRowRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [activity.name]);

  const rows = getLevelRows({
    completedLevels: progress.completedLevels,
    nextLevel: progress.nextLevel,
    totalLevels: activity.levels,
  });
  const finished = rows.every((row) => row.status === 'completed');
  const titleId = `gcompris-levels-${activity.name}`;

  // Capped to what is visible so only the level list scrolls, never the header or the footer note.
  const card = (
    <div
      role={docked ? 'region' : 'dialog'}
      aria-modal={docked ? undefined : 'true'}
      aria-labelledby={titleId}
      className={`flex flex-col overflow-y-auto rounded-2xl border border-gray-100 bg-white p-5 ${
        docked ? 'max-h-[calc(100vh-11rem)] shadow-sm' : 'w-full max-w-md max-h-[90vh] shadow-2xl'
      }`}
    >
      <div className="flex shrink-0 items-start gap-3 mb-4">
        <img src={gcomprisIconUrl(activity.name)} alt="" className="h-16 w-16 shrink-0 object-contain" />
        <div className="flex-1 min-w-0">
          <h2 id={titleId} className="text-xl font-extrabold leading-tight text-slate-900">{activity.title}</h2>
          <p className="mt-0.5 text-sm font-medium text-slate-600">{activity.description}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close levels"
          className="shrink-0 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
        >
          <X size={22} />
        </button>
      </div>

      {/* The negative margin / padding pair keeps the rows' shadows from being clipped by the scroll box. */}
      <ul className="-mx-1 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-1 py-0.5">
        {rows.map((row) => (
          <LevelRow
            key={row.level}
            {...row}
            coins={coinsForLevel(row.level)}
            onPlay={onPlay}
            rowRef={row.status === 'play' ? playRowRef : undefined}
          />
        ))}
      </ul>

      {finished && (
        <button
          type="button"
          onClick={onPlay}
          className="mt-3 w-full shrink-0 inline-flex items-center justify-center gap-1.5 rounded-xl bg-indigo-600 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-indigo-700 transition-colors"
        >
          <Play size={15} fill="currentColor" /> Play again
        </button>
      )}

      <div className="mt-4 flex shrink-0 items-start gap-3 rounded-xl bg-sky-50 px-4 py-3 text-sm font-medium text-slate-700">
        <Info size={20} className="mt-0.5 shrink-0 text-sky-600" />
        <p>
          {finished && activity.levels
            ? 'You completed every level. Great work!'
            : sameEveryLevel === null
              ? 'Complete each level to earn the coins shown beside it. Keep practicing and unlock the next level!'
              : sameEveryLevel > 0
                ? `Complete each level to earn ${sameEveryLevel} ${sameEveryLevel === 1 ? 'coin' : 'coins'}. Keep practicing and unlock the next level!`
                : 'Complete each level to unlock the next one. Keep practicing!'}
        </p>
      </div>
    </div>
  );

  if (docked) return card;

  return createPortal(
    <div
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
    >
      {card}
    </div>,
    document.body
  );
}
